import type { ReactElement } from 'react';
import { Box, Button, ButtonGroup, Heading, Page, Paragraph } from '@zillow/constellation-10';
import { SpotIllustration } from '../SpotIllustration';

/**
 * Page-level error component. Renders standalone — the only siblings it should have
 * inside the route tree are the top-level header (`B2BLayout`/`PageFrame`/etc.) and/or
 * the left-side navigation. Do NOT nest it inside a Card, Modal, Popover, or any other
 * content container; do NOT render it alongside other page content. If you only need
 * to communicate that one section/card on a page failed, use a Constellation `Alert` or
 * `InlineFeedback` instead — those are the right components for inline / partial failures
 * where the rest of the page is fine.
 *
 * **Canonical copy — use these exact strings for 404 and 500 errors.** Do not paraphrase
 * or substitute your own copy. Content design has signed off on these and the whole point
 * of the cohesion work is that every ZRM app shows the same words on these screens. Use
 * them verbatim, including the curly apostrophes (’):
 *
 * - **404 / not found** — `errorMessage`: "We can’t find that page" / `errorDetails`: "That
 *   page may have moved or no longer exists. Let’s get you back on track." / **Action: single
 *   primary `<Link href="/">Go to Rental Manager</Link>` only — do NOT add a secondary
 *   "Contact support" button.** A missing route is a navigation problem, not a support
 *   escalation; pointing users to support muddies the recovery path.
 * - **500 / server error** — `errorMessage`: "Something went wrong on our end" /
 *   `errorDetails`: "Try refreshing the page. If that doesn’t work, check back in a few minutes."
 *   / **Actions: primary `<Link href="/">Go to Rental Manager</Link>` (filled) + secondary
 *   `<a href="/support">Contact support</a>` (outlined).** A server error may be persistent
 *   and warrants the escalation path.
 * - Adapt the destination URLs for your app, but keep the button labels exactly as written.
 *
 * For other error states (auth-expired, account suspended, transient retry, etc.), follow the
 * same anatomy but coordinate copy with content design before shipping.
 *
 * **Background:** uses `bg.screen.neutral` — Constellation's semantic token for page-level
 * screen backgrounds. Resolves to white (`colors.white`) in the zillow theme. Use this rather
 * than `bg.neutral` for page-level error states; reserve `bg.neutral` for generic neutral
 * surfaces (cards, sheets) and use `bg.screen.*` tokens for full-screen backdrops like this one.
 */
export type ErrorPageProps = {
  /**
   * Optional override for the spot illustration. Accepts the result of importing
   * a `.svg` file. Defaults to the Digital Library `error` spot illustration
   * (`<SpotIllustration name="error" />`). The component always renders it as a decorative
   * 120×120 `<img>` with `alt=""` inside an `aria-hidden` wrapper.
   *
   * Pass an SVG import — not an arbitrary ReactNode — so the standard 120×120
   * spot-illustration anatomy stays consistent across every error page.
   *
   * @example
   * import mySpotIllustration from './my-spot-illustration.svg';
   * <ErrorPage illustrationSrc={mySpotIllustration} ... />
   */
  illustrationSrc?: { src: string };
  /**
   * Optional custom error message. Defaults to "Something went wrong"
   */
  errorMessage?: string;
  /**
   * Optional additional details about the error
   */
  errorDetails?: React.ReactNode;
  /**
   * Optional primary recovery element rendered as a brand-filled Button via Constellation `asChild`.
   * Pass the semantically-correct element: `<a>` / `<Link>` for navigation, `<button onClick>` for stateful retry.
   * Do NOT wrap in `<Button>` — the component handles styling.
   * On desktop sits on the right of the ButtonGroup when a secondaryAction is also provided.
   * On mobile stacks on top.
   * @example <Link href="/">Return home</Link>
   * @example <a href="/">Return home</a>
   * @example <button type="button" onClick={() => refetch()}>Try again</button>
   */
  action?: React.ReactNode;
  /**
   * Optional secondary recovery element rendered as a brand-outlined Button.
   * Same `asChild` rules as `action` — pass `<a>`/`<Link>` for navigation or `<button onClick>` for stateful actions.
   * Use alongside `action` for the canonical two-button error page (e.g., "Contact support" + "Go to Rental Manager").
   * On desktop sits on the left of the ButtonGroup; on mobile stacks below the primary.
   * @example <a href="/support">Contact support</a>
   * @example <button type="button" onClick={openContactModal}>Contact support</button>
   */
  secondaryAction?: React.ReactNode;
};

export function ErrorPage({
  illustrationSrc,
  errorDetails,
  errorMessage = 'Something went wrong',
  action,
  secondaryAction,
}: ErrorPageProps): ReactElement {
  const imageSrc = illustrationSrc?.src;
  const hasActions = Boolean(action || secondaryAction);
  const hasBothActions = Boolean(action && secondaryAction);

  return (
    <Page.Root
      css={{
        backgroundColor: 'bg.screen.neutral',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Page.Content
        fluid
        css={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Box aria-hidden="true" css={{ marginBottom: 'default' }}>
          {imageSrc ? (
            <img src={imageSrc} alt="" width={120} height={120} />
          ) : (
            <SpotIllustration name="error" size={120} />
          )}
        </Box>
        <Heading level={1} textStyle="heading-xs" css={{ marginBottom: errorDetails ? 'tightest' : 'default' }}>
          {errorMessage}
        </Heading>
        {errorDetails ? (
          <Paragraph css={{ marginBottom: hasActions ? 'default' : undefined }}>{errorDetails}</Paragraph>
        ) : null}
        {hasBothActions && (
          <ButtonGroup
            aria-label="Error page actions"
            orientation="horizontal"
            css={{
              flexDirection: { base: 'column', md: 'row-reverse' },
              alignItems: { base: 'stretch', md: 'start' },
              justifyContent: { md: 'center' },
            }}
          >
            <Button tone="brand" emphasis="filled" asChild>
              {action}
            </Button>
            <Button tone="brand" emphasis="outlined" asChild>
              {secondaryAction}
            </Button>
          </ButtonGroup>
        )}
        {action && !secondaryAction && (
          <Button tone="brand" emphasis="filled" asChild>
            {action}
          </Button>
        )}
        {!action && secondaryAction && (
          <Button tone="brand" emphasis="outlined" asChild>
            {secondaryAction}
          </Button>
        )}
      </Page.Content>
    </Page.Root>
  );
}
